import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import React, { useState, useMemo } from "react";
import {
  ArrowLeft,
  MagnifyingGlass,
  Funnel,
  X,
  MapPin,
  Percent,
  Gift,
  Lock,
  ArrowRight,
} from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { getLocalizedText } from "@shared/lib/utils";

export const Route = createFileRoute("/offers-preview")({
  component: OffersPreviewPage,
});

function OffersPreviewPage() {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"discount" | "popularity">("discount");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);

  const offers = useQuery(api.offers.getActiveOffers, {});
  const categories = useQuery(api.offers.getCategories, {});

  // Filtrare și sortare
  const filteredAndSortedOffers = useMemo(() => {
    if (!offers) return [];

    let filtered = offers.filter((offer) => {
      // Filtrare pe categorie
      if (selectedCategory && offer.category?.slug !== selectedCategory) {
        return false;
      }

      // Filtrare pe search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const partnerName = offer.partner?.name?.toLowerCase() || "";
        const description = getLocalizedText(offer.description, currentLang).toLowerCase();
        const shortDesc = getLocalizedText(offer.shortDescription, currentLang).toLowerCase();
        const categoryName = offer.category?.name?.toLowerCase() || "";

        if (
          !partnerName.includes(query) &&
          !description.includes(query) &&
          !shortDesc.includes(query) &&
          !categoryName.includes(query)
        ) {
          return false;
        }
      }

      return true;
    });

    // Sortare
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "discount":
          const discountA = a.discountType === "percent" ? (a.discountValue || 0) : 0;
          const discountB = b.discountType === "percent" ? (b.discountValue || 0) : 0;
          return discountB - discountA;
        case "popularity":
          return (b.totalRedemptions || 0) - (a.totalRedemptions || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [offers, selectedCategory, searchQuery, sortBy, currentLang]);

  const selectedOfferData = useMemo(() => {
    if (!selectedOffer || !offers) return null;
    return offers.find((o) => o._id === selectedOffer);
  }, [selectedOffer, offers]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-text hover:text-primary transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">{currentLang === "ro" ? "Înapoi" : "Back"}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link
                to="/auth/login"
                className="px-4 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
              >
                {t("auth:login.title")}
              </Link>
              <Link
                to="/auth/register"
                className="px-4 py-2 text-sm font-medium bg-primary-gradient text-white rounded-lg hover:shadow-lg hover:shadow-primary/30 transition-all"
              >
                {t("auth:register.title")}
              </Link>
            </div>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-text mb-4">
            {currentLang === "ro" ? "Oferte disponibile" : "Available offers"}
          </h1>

          {/* Search */}
          <div className="relative mb-4">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              placeholder={currentLang === "ro" ? "Caută oferte..." : "Search offers..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-white border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-background-dark rounded"
              >
                <X size={16} className="text-text-muted" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
                showFilters || selectedCategory || sortBy !== "discount"
                  ? "bg-primary text-white"
                  : "bg-white border border-border text-text"
              }`}
            >
              <Funnel size={16} />
              {currentLang === "ro" ? "Filtre" : "Filters"}
            </button>

            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm whitespace-nowrap"
              >
                {categories?.find((c) => c.slug === selectedCategory)?.name}
                <X size={12} />
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-border space-y-3">
              {/* Categories */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  {currentLang === "ro" ? "Categorie" : "Category"}
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${
                      !selectedCategory
                        ? "bg-primary text-white"
                        : "bg-white border border-border text-text"
                    }`}
                  >
                    {currentLang === "ro" ? "Toate" : "All"}
                  </button>
                  {categories?.map((category) => (
                    <button
                      key={category._id}
                      onClick={() => setSelectedCategory(category.slug)}
                      className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
                        selectedCategory === category.slug
                          ? "bg-primary text-white"
                          : "bg-white border border-border text-text"
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  {currentLang === "ro" ? "Sortează după" : "Sort by"}
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSortBy("discount")}
                    className={`px-3 py-1.5 rounded-lg text-sm ${
                      sortBy === "discount"
                        ? "bg-primary text-white"
                        : "bg-white border border-border text-text"
                    }`}
                  >
                    {currentLang === "ro" ? "Reducere" : "Discount"}
                  </button>
                  <button
                    onClick={() => setSortBy("popularity")}
                    className={`px-3 py-1.5 rounded-lg text-sm ${
                      sortBy === "popularity"
                        ? "bg-primary text-white"
                        : "bg-white border border-border text-text"
                    }`}
                  >
                    {currentLang === "ro" ? "Popularitate" : "Popularity"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto max-w-6xl px-4 py-6">
        {offers === undefined ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : filteredAndSortedOffers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlass size={24} className="text-text-muted" />
            </div>
            <h2 className="text-lg font-semibold text-text mb-2">
              {currentLang === "ro" ? "Nu s-au găsit oferte" : "No offers found"}
            </h2>
            <p className="text-text-muted mb-6">
              {currentLang === "ro"
                ? "Încearcă să modifici filtrele sau termenii de căutare"
                : "Try adjusting your filters or search terms"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAndSortedOffers.map((offer) => (
              <OfferPreviewCard
                key={offer._id}
                offer={offer}
                currentLang={currentLang}
                onCardClick={() => setSelectedOffer(offer._id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Invite Required Modal */}
      {selectedOfferData && (
        <InviteRequiredModal
          offer={selectedOfferData}
          currentLang={currentLang}
          onClose={() => setSelectedOffer(null)}
        />
      )}
    </div>
  );
}

// Componentă pentru carduri de ofertă în preview
const OfferPreviewCard = React.memo(function OfferPreviewCard({
  offer,
  currentLang,
  onCardClick,
}: {
  offer: any;
  currentLang: "ro" | "en";
  onCardClick: () => void;
}) {
  const { t } = useTranslation();
  const description = useMemo(
    () => getLocalizedText(offer.shortDescription || offer.description, currentLang),
    [offer.shortDescription, offer.description, currentLang]
  );
  const benefitText = useMemo(
    () => getLocalizedText(offer.benefitText, currentLang),
    [offer.benefitText, currentLang]
  );

  return (
    <div
      onClick={onCardClick}
      className=" cursor-pointer border bordeflex items-center gap-4"
    >
      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
        {offer.discountType === "percent" ? (
          <Percent size={20} className="text-primary" />
        ) : (
          <Gift size={20} className="text-primary" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-semibold text-text truncate">{offer.partner?.name}</p>
          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full whitespace-nowrap">
            {t("common:preview.inviteOnly")}
          </span>
        </div>
        <p className="text-sm text-text-light truncate">{description}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-text-muted">{offer.category?.name}</span>
          {offer.partner?.city && (
            <>
              <span className="text-xs text-text-muted">•</span>
              <div className="flex items-center gap-1 text-xs text-text-muted">
                <MapPin size={12} />
                <span className="truncate">{offer.partner.city}</span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex-shrink-0">
        <span className="px-3 py-1 bg-success/10 text-success font-bold rounded-lg text-sm">
          {offer.discountType === "percent"
            ? `${offer.discountValue}%`
            : benefitText || (currentLang === "ro" ? "Beneficiu" : "Benefit")}
        </span>
      </div>
    </div>
  );
});

// Modal pentru invitație necesară
function InviteRequiredModal({
  offer,
  currentLang,
  onClose,
}: {
  offer: any;
  currentLang: "ro" | "en";
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRegister = () => {
    navigate({ to: "/auth/register" });
  };

  const handleHowItWorks = () => {
    onClose();
    // Scroll to FAQ section on homepage
    window.location.href = "/#faq";
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Lock size={20} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text">
              {offer.partner?.name || (currentLang === "ro" ? "Ofertă" : "Offer")}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-dark rounded-lg transition-colors"
          >
            <X size={20} className="text-text-muted" />
          </button>
        </div>

        <p className="text-text-light mb-6">
          {t("common:preview.inviteRequired")}
        </p>

        <div className="space-y-3">
          <button
            onClick={handleRegister}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-gradient text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all"
          >
            {t("common:preview.enterInviteCode")}
            <ArrowRight size={20} />
          </button>
          <button
            onClick={handleHowItWorks}
            className="w-full px-6 py-3 bg-white border border-border text-text rounded-xl font-medium hover:bg-background-dark transition-colors"
          >
            {t("common:preview.howInvitesWork")}
          </button>
        </div>
      </div>
    </div>
  );
}
